import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatChipsModule } from '@angular/material/chips';
import { MatTabsModule } from '@angular/material/tabs';
import { MatStepperModule } from '@angular/material/stepper';
import { MatListModule } from '@angular/material/list';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatTableModule } from '@angular/material/table';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Subject, takeUntil, forkJoin, of } from 'rxjs';
import { catchError, finalize, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';

// Services
import { EstadoEventoAgendaService } from '@services/estadoEventoAgenda/estadoEventoAgenda.service';
import { TipoEventoAgendaService } from '@services/tipoEventoAgenda/tipoEventoAgenda.service';
import { EventoAgendaService } from '@services/eventoAgenda/eventoAgenda.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';

// Components
import { ButtonCancelDialogComponent } from '../../button-cancel-dialog/button-cancel-dialog.component';
import { ButtonAddComponent } from '../../button-add/button-add.component';
import { ButtonCloseDialogComponent } from '../../button-close-dialog/button-close-dialog.component';
import { DialogDeleteComponent } from '../../dialog-delete/dialog-delete.component';
import { DialogRepeatEventComponent } from './dialog-repeat-event/dialog-repeat-event.component';

// Models
import { BaseDto } from '@models/base/baseDto.model';
import { CreateEventoAgendaRequest } from '@models/eventoAgenda/createEventoAgendaRequest.model';
import { UpdateEventoAgendaRequest } from '@models/eventoAgenda/updateEventoAgendaRequest.model';
import { EventoAgendaDto } from '@models/eventoAgenda/eventoAgendaDto.model';
import { AccionesDialog } from '@models/base/identificadores.model';
import { AvatarInicialesComponent } from '@component/avatar-iniciales/avatar-iniciales.component';
import { ComponentModule } from "../../../modules/component.module";
import { ButtonCloseComponent } from "../../button-close/button-close.component";
import { ButtonSaveComponent } from '@component/button-save/button-save.component';

// Interfaces
interface TimeZoneOption {
  label: string;
  value: string;
}

interface ReminderOption {
  label: string;
  value: number;
}

interface EventDialogData {
  start: any;
  end: any;
  esNuevo: boolean;
  esEliminar?: boolean;
  item: Partial<EventoAgendaDto>;
  esVista?: boolean;
  esEditar?: boolean;
  idExpediente?: number;
}

// Constants
const SPINNER_NAMES = {
  TIPOS_EVENTO: 'spTiposEventoAgenda',
  ESTADOS_EVENTO: 'spEstadoEventoAgenda',
  EVENTO_DIALOG: 'spEventoDialogCalendar',
} as const;

const TIME_CONSTANTS = {
  START_OF_DAY: '00:00:00',
  END_OF_DAY: '23:59:59',
  DEFAULT_SECONDS: ':00'
} as const;

@Component({
  selector: 'app-event-dialog-calendar-component',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    // Material Modules
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
    MatCheckboxModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    MatTableModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatMenuModule,
    MatExpansionModule,
    MatRadioModule,
    MatProgressBarModule,
    MatChipsModule,
    MatTabsModule,
    MatStepperModule,
    MatListModule,
    MatToolbarModule,
    MatBadgeModule,
    NgxSpinnerModule,
    // Custom Components
    ButtonCancelDialogComponent,
    ButtonAddComponent,
    ButtonCloseDialogComponent,
    AvatarInicialesComponent,
    ComponentModule,
    ButtonSaveComponent,
    ButtonCloseComponent
],
  templateUrl: './event-dialog-calendar-component.component.html',
  styleUrl: './event-dialog-calendar-component.component.css'
})
export class EventDialogCalendarComponentComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // Form
  form!: FormGroup;

  // Data
  listaTiposEvento: BaseDto[] = [];
  estadosEventoAgenda: BaseDto[] = [];
  eventoAgendaDto?: EventoAgendaDto;
  timeZoneList: TimeZoneOption[] = [];
  reminderOptions: ReminderOption[] = [
    { label: 'No recordármelo', value: 0 },
    { label: 'En el momento del evento', value: 0 },
    { label: '5 minutos antes', value: 5 },
    { label: '15 minutos antes', value: 15 },
    { label: '30 minutos antes', value: 30 },
    { label: '1 hora antes', value: 60 },
    { label: '2 horas antes', value: 120 },
    { label: '12 horas antes', value: 720 },
    { label: '1 día antes', value: 1440 },
    { label: '1 semana antes', value: 10080 }
  ];

  // Tab management
  selectedTabIndex: number = 0;

  constructor(
    private readonly fb: FormBuilder,
    private readonly tipoEventoAgendaService: TipoEventoAgendaService,
    private readonly estadoEventoAgendaService: EstadoEventoAgendaService,
    private readonly eventoAgendaService: EventoAgendaService,
    private readonly spinner: NgxSpinnerService,
    private readonly snackBar: SnackBarService,
    private readonly dialog: MatDialog,
    public readonly dialogRef: MatDialogRef<EventDialogCalendarComponentComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: EventDialogData,
    private cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.initializeComponent();
    setTimeout(() => this.cdr.detectChanges(), 0);
      console.log('Data on init:', this.data); // Debug
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeComponent(): void {
    this.loadInitialData();
    this.initializeTimeZones();
    this.initializeForm();

    if (this.data.esNuevo || this.data.esVista) {
      this.getById();
    }
  }

  private loadInitialData(): void {
    const tiposEventoSpinner = this.spinner.show(SPINNER_NAMES.TIPOS_EVENTO);
    const estadosEventoSpinner = this.spinner.show(SPINNER_NAMES.ESTADOS_EVENTO);

    forkJoin({
      tiposEvento: this.tipoEventoAgendaService.getAll(),
      estadosEvento: this.estadoEventoAgendaService.getAll()
    }).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        this.snackBar.showError(error, 'Error al cargar datos iniciales');
        return of({ tiposEvento: [], estadosEvento: [] });
      }),
      finalize(() => {
        this.spinner.hide(SPINNER_NAMES.TIPOS_EVENTO);
        this.spinner.hide(SPINNER_NAMES.ESTADOS_EVENTO);
      })
    ).subscribe({
      next: ({ tiposEvento, estadosEvento }) => {
        this.listaTiposEvento = tiposEvento;
        this.estadosEventoAgenda = estadosEvento;
      }
    });
  }

  private initializeTimeZones(): void {
    this.timeZoneList = Intl.supportedValuesOf('timeZone').map(tz => ({
      label: tz.replace(/_/g, ' '),
      value: tz
    }));
  }

  private initializeForm(): void {
    const item = this.data.item;

    // Si el evento es nuevo, tomamos las fechas seleccionadas, si no, las que ya tiene el item
    const fechaInicio = this.data.esNuevo ? this.data.start : item?.fechaInicio || new Date();
    const fechaFin = this.data.esNuevo ? this.data.end || this.data.start : item?.fechaFin || new Date();

    this.form = this.fb.group({
      Id: [item?.id || null],
      IdExpediente: [this.data?.idExpediente || null],
      Titulo: [
        { value: item?.titulo || '', disabled: this.data.esVista && !this.data.esEditar },
        [Validators.required, Validators.maxLength(255)]
      ],
      FechaInicio: [
        { value: fechaInicio, disabled: this.data.esVista && !this.data.esEditar },
        [Validators.required]
      ],
      FechaFin: [
        { value: fechaFin, disabled: this.data.esVista && !this.data.esEditar }
      ],
      HoraInicio: [
        { value: item?.horaInicio || '09:00', disabled: this.data.esVista && !this.data.esEditar },
        [Validators.required]
      ],
      HoraFin: [
        { value: item?.horaFin || '10:00', disabled: this.data.esVista && !this.data.esEditar },
        [Validators.required]
      ],
      IdTipoEvento: [
        { value: item?.tipoEventoAgenda?.id || '', disabled: this.data.esVista && !this.data.esEditar },
        [Validators.required]
      ],
      IdEstadoEvento: [
        { value: item?.estadoEventoAgenda?.id || '', disabled: this.data.esVista && !this.data.esEditar },
        [Validators.required]
      ],
      EsTodoElDia: [
        { value: item?.esTodoElDia || false, disabled: this.data.esVista && !this.data.esEditar }
      ],
      Observaciones: [
        { value: item?.observaciones || '', disabled: this.data.esVista && !this.data.esEditar },
        [Validators.maxLength(1000)]
      ],
      Lugar: [
        { value: item?.lugar || '', disabled: this.data.esVista && !this.data.esEditar },
        [Validators.maxLength(255)]
      ],
      EsEventoPresencial: [
        { value: item?.esEventoPresencial || false, disabled: this.data.esVista && !this.data.esEditar }
      ],
      ZonaHoraria: [
        { value: item?.zonaHoraria || 'America/Argentina/Buenos_Aires', disabled: this.data.esVista && !this.data.esEditar }
      ],
      AvisoNMinutosAntes: [
        { value: item?.avisoNMinutosAntes || 0, disabled: this.data.esVista && !this.data.esEditar },
        [Validators.min(0)]
      ],
      DiasSemana: [
        { value: item?.diasSemana || '', disabled: this.data.esVista && !this.data.esEditar }
      ],
      UnidadRepeticion: [
        { value: item?.unidadRepeticion || '', disabled: this.data.esVista && !this.data.esEditar }
      ],
      Recordatorio: [
        { value: item?.recordatorio || 0, disabled: this.data.esVista && !this.data.esEditar }
      ],
      Prioridad: [
        { value: item?.prioridad || 1, disabled: this.data.esVista && !this.data.esEditar }
      ],
      Descripcion: [
        { value: item?.descripcion || '', disabled: this.data.esVista && !this.data.esEditar },
        [Validators.maxLength(2000)]
      ],
      NivelAvance: [
        { value: item?.nivelAvance || 0, disabled: this.data.esVista && !this.data.esEditar },
        [Validators.min(0), Validators.max(100)]
      ],
      Duracion: [
        { value: item?.duracion || null, disabled: this.data.esVista && !this.data.esEditar },
        [Validators.min(1)]
      ],
      EsEditable: [
        { value: item?.esEditable !== false, disabled: this.data.esVista && !this.data.esEditar }
      ],
      EsRecurrente: [
        { value: item?.esRecurrente || false, disabled: this.data.esVista && !this.data.esEditar }
      ],
      EsEventoVirtual: [
        { value: !!item?.url, disabled: this.data.esVista && !this.data.esEditar }
      ],
      Url: [
        { value: item?.url || '', disabled: this.data.esVista && !this.data.esEditar }
      ],
      ColorFondo: [
        { value: item?.colorFondo || '#ffffff', disabled: this.data.esVista && !this.data.esEditar }
      ],
      ColorBorde: [
        { value: item?.colorBorde || '#cccccc', disabled: this.data.esVista && !this.data.esEditar }
      ],
      ColorTexto: [
        { value: item?.colorTexto || '#000000', disabled: this.data.esVista && !this.data.esEditar }
      ],
      EsAvisoAPartes: [
        { value: item?.esAvisoAPartes || false, disabled: this.data.esVista && !this.data.esEditar }
      ]
    }, {
      validators: [this.validarFechas(), this.validarHoras()]
    });

    this.setupFormSubscriptions();
    this.updateTimeFieldsVisibility();
  }

  private setupFormSubscriptions(): void {
    this.form.get('EsTodoElDia')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.onToggleTodoElDia());

    // Validación en tiempo real de fechas
    this.form.get('FechaInicio')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.validateDateTimeRange();
        // this.getCurrentEventsByDate();
      });

    this.form.get('FechaFin')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.validateDateTimeRange());

    this.form.get('HoraInicio')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.validateDateTimeRange());

    this.form.get('HoraFin')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.validateDateTimeRange());

    // Actualización automática en modo edición rápida (solo si no es vista)
    if (!this.data.esVista && this.data.item?.id) {
      this.setupQuickUpdateSubscriptions();
    }
  }

  private setupQuickUpdateSubscriptions(): void {
    // Suscripciones para actualización automática de campos individuales
    const fieldsToWatch = [
      'Titulo', 'Lugar', 'IdTipoEvento', 'IdEstadoEvento', 'Recordatorio'
    ];

    fieldsToWatch.forEach(fieldName => {
      this.form.get(fieldName)?.valueChanges
        .pipe(
          takeUntil(this.destroy$),
          debounceTime(1000), // Esperar 1 segundo después del último cambio
          distinctUntilChanged()
        )
        .subscribe(() => {
          if (this.form.get(fieldName)?.valid && this.canEdit) {
            this.quickUpdateField(fieldName);
          }
        });
    });

    // Para observaciones, usar un debounce más largo
    this.form.get('Observaciones')?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(2000),
        distinctUntilChanged()
      )
      .subscribe(() => {
        if (this.form.get('Observaciones')?.valid && this.canEdit) {
          this.quickUpdateField('Observaciones');
        }
      });
  }


  private updateTimeFieldsVisibility(): void {
    const esTodoElDia = this.form.get('EsTodoElDia')?.value;
    const horaInicioControl = this.form.get('HoraInicio');
    const horaFinControl = this.form.get('HoraFin');

    if (esTodoElDia) {
      horaInicioControl?.disable();
      horaFinControl?.disable();
      horaInicioControl?.setValue(TIME_CONSTANTS.START_OF_DAY);
      horaFinControl?.setValue(TIME_CONSTANTS.END_OF_DAY);
    } else {
      if (!this.data.esVista || this.data.esEditar) {
        horaInicioControl?.enable();
        horaFinControl?.enable();
      }
    }
  }

  onToggleTodoElDia(): void {
    const esTodoElDia = this.form.get('EsTodoElDia')?.value;
    this.updateValidatorsAndValues(esTodoElDia);
  }

  private updateValidatorsAndValues(esTodoElDia: boolean): void {
    const fechaFinControl = this.form.get('FechaFin');
    const horaInicioControl = this.form.get('HoraInicio');
    const horaFinControl = this.form.get('HoraFin');

    if (esTodoElDia) {
      this.setupAllDayMode(fechaFinControl, horaInicioControl, horaFinControl);
    } else {
      this.setupTimedMode(fechaFinControl, horaInicioControl, horaFinControl);
    }

    [fechaFinControl, horaInicioControl, horaFinControl].forEach(control => {
      control?.updateValueAndValidity();
    });
  }

  private setupAllDayMode(fechaFinControl: any, horaInicioControl: any, horaFinControl: any): void {
    fechaFinControl?.setValidators([Validators.required]);
    horaInicioControl?.clearValidators();
    horaFinControl?.clearValidators();

    horaInicioControl?.setValue(TIME_CONSTANTS.START_OF_DAY);
    horaFinControl?.setValue(TIME_CONSTANTS.END_OF_DAY);
    horaInicioControl?.disable();
    horaFinControl?.disable();

    if (!fechaFinControl?.value) {
      const fechaInicio = this.form.get('FechaInicio')?.value;
      if (fechaInicio) {
        fechaFinControl?.setValue(fechaInicio);
      }
    }
  }

  private setupTimedMode(fechaFinControl: any, horaInicioControl: any, horaFinControl: any): void {
    fechaFinControl?.clearValidators();
    horaInicioControl?.setValidators([Validators.required]);
    horaFinControl?.setValidators([Validators.required]);

    fechaFinControl?.setValue(null);

    if (!this.data.esVista || this.data.esEditar) {
      horaInicioControl?.enable();
      horaFinControl?.enable();
    }

    if (!horaInicioControl?.value) {
      horaInicioControl?.setValue('09:00');
    }
    if (!horaFinControl?.value) {
      horaFinControl?.setValue('10:00');
    }
  }

  private validateDateTimeRange(): void {
    const esTodoElDia = this.form.get('EsTodoElDia')?.value;
    const fechaInicio = this.form.get('FechaInicio')?.value;

    if (esTodoElDia) {
      const fechaFin = this.form.get('FechaFin')?.value;
      if (fechaInicio && fechaFin && fechaFin < fechaInicio) {
        this.form.get('FechaFin')?.setErrors({ 'dateRange': true });
      } else {
        const currentErrors = this.form.get('FechaFin')?.errors;
        if (currentErrors?.['dateRange']) {
          delete currentErrors['dateRange'];
          this.form.get('FechaFin')?.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
        }
      }
    } else {
      const horaInicio = this.form.get('HoraInicio')?.value;
      const horaFin = this.form.get('HoraFin')?.value;

      if (horaInicio && horaFin && horaFin <= horaInicio) {
        this.form.get('HoraFin')?.setErrors({ 'timeRange': true });
      } else {
        const currentErrors = this.form.get('HoraFin')?.errors;
        if (currentErrors?.['timeRange']) {
          delete currentErrors['timeRange'];
          this.form.get('HoraFin')?.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
        }
      }
    }
  }

  private buildCreateRequest(): CreateEventoAgendaRequest {
    const formValue = this.form.value;
    const request = new CreateEventoAgendaRequest();

    Object.assign(request, {
      titulo: formValue.Titulo,
      fechaInicio: formValue.FechaInicio,
      idExpediente: formValue.IdExpediente,
      fechaFin: formValue.FechaFin,
      horaInicio: formValue.EsTodoElDia ? TIME_CONSTANTS.START_OF_DAY : formValue.HoraInicio,
      horaFin: formValue.EsTodoElDia ? TIME_CONSTANTS.END_OF_DAY : formValue.HoraFin,
      idTipoEventoAgenda: formValue.IdTipoEvento,
      idEstadoEventoAgenda: formValue.IdEstadoEvento,
      esTodoElDia: formValue.EsTodoElDia,
      observaciones: formValue.Observaciones,
      lugar: formValue.Lugar || '',
      esEventoPresencial: formValue.EsEventoPresencial,
      zonaHoraria: formValue.ZonaHoraria,
      avisoNMinutosAntes: formValue.AvisoNMinutosAntes || 0,
      diasSemana: formValue.DiasSemana || '',
      unidadRepeticion: formValue.UnidadRepeticion || '',
      recordatorio: formValue.Recordatorio || '',
      // Default values
      asignadoA: null,
      url: null,
      classNames: null,
      esEditable: true,
      colorFondo: '#ffffff',
      colorBorde: '#cccccc',
      colorTexto: '#000000',
      prioridad: 0,
      nivelAvance: 0,
      comentarios: '',
      esRecurrente: false,
      frecuenciaRecurrente: '',
      duracion: null,
      esAvisoAPartes: false
    });

    return request;
  }

  private buildUpdateRequest(): UpdateEventoAgendaRequest {
    const formValue = this.form.value;
    const item = this.data.item;
    const request = new UpdateEventoAgendaRequest();

    Object.assign(request, {
      id: item?.id,
      titulo: formValue.Titulo,
      fechaInicio: formValue.FechaInicio,
      fechaFin: formValue.FechaFin,
      horaInicio: formValue.EsTodoElDia ? TIME_CONSTANTS.START_OF_DAY : formValue.HoraInicio,
      horaFin: formValue.EsTodoElDia ? TIME_CONSTANTS.END_OF_DAY : formValue.HoraFin,
      idTipoEventoAgenda: formValue.IdTipoEvento,
      idEstadoEventoAgenda: formValue.IdEstadoEvento,
      esTodoElDia: formValue.EsTodoElDia,
      observaciones: formValue.Observaciones,
      lugar: formValue.Lugar || '',
      esEventoPresencial: formValue.EsEventoPresencial,
      zonaHoraria: formValue.ZonaHoraria,
      avisoNMinutosAntes: formValue.AvisoNMinutosAntes || 0,
      diasSemana: formValue.DiasSemana || '',
      unidadRepeticion: formValue.UnidadRepeticion || '',
      recordatorio: formValue.Recordatorio || '',
      // Preserve existing values
      asignadoA: item?.asignadoA,
      descripcion: formValue.Descripcion || '',
      url: item?.url,
      classNames: item?.classNames,
      esEditable: item?.esEditable,
      colorFondo: item?.colorFondo,
      colorBorde: item?.colorBorde,
      colorTexto: item?.colorTexto,
      idExpediente: item?.expediente?.id,
      prioridad: item?.prioridad,
      nivelAvance: item?.nivelAvance,
      comentarios: item?.comentarios,
      esRecurrente: item?.esRecurrente,
      frecuenciaRecurrente: item?.frecuenciaRecurrente,
      duracion: item?.duracion,
      esAvisoAPartes: item?.esAvisoAPartes
    });

    return request;
  }

  create(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      this.snackBar.showError('Por favor, complete todos los campos requeridos', 'Formulario inválido');
      return;
    }

    this.spinner.show(SPINNER_NAMES.EVENTO_DIALOG);
    const request = this.buildCreateRequest();

    this.eventoAgendaService.create(request)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.spinner.hide(SPINNER_NAMES.EVENTO_DIALOG))
      )
      .subscribe({
        next: (data) => {
          this.snackBar.showSuccess('Evento creado exitosamente.', 'Éxito');
          this.dialogRef.close({ event: AccionesDialog.Agregar, data: data });
        },
        error: (error) => {
          this.snackBar.showError(error, 'Error al crear el evento.');
        }
      });
  }

  update(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      this.snackBar.showError('Por favor, complete todos los campos requeridos', 'Formulario inválido');
      return;
    }

    this.spinner.show(SPINNER_NAMES.EVENTO_DIALOG);
    const request = this.buildUpdateRequest();

    this.eventoAgendaService.update(request)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.spinner.hide(SPINNER_NAMES.EVENTO_DIALOG))
      )
      .subscribe({
        next: (data) => {
          this.snackBar.showSuccess('Evento actualizado exitosamente.', 'Éxito');
          this.dialogRef.close({ event: AccionesDialog.Modificar, data: data });
        },
        error: (error) => {
          this.snackBar.showError(error, 'Error al actualizar el evento.');
        }
      });
  }

  private deleteEvent(id: number): void {
    this.spinner.show(SPINNER_NAMES.EVENTO_DIALOG);

    this.eventoAgendaService.delete(id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.spinner.hide(SPINNER_NAMES.EVENTO_DIALOG))
      )
      .subscribe({
        next: (data) => {
          this.snackBar.showSuccess('Evento eliminado.', 'Éxito');
          this.dialogRef.close({ event: AccionesDialog.Eliminar, data: data });
        },
        error: (error) => {
          this.snackBar.showError(error, 'Error al eliminar el evento.');
        }
      });
  }

  eliminarEvento(): void {
    const dialogRef = this.dialog.open(DialogDeleteComponent, {
      width: '500px',
      data: {
        item: this.data.item,
        title: 'Eliminar Evento',
        message: '¿Está seguro de que desea eliminar este evento?'
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        if (result?.event === AccionesDialog.Eliminar && this.data.item?.id) {
          this.deleteEvent(this.data.item.id);
        }
      });
  }

  eliminar(): void {
    if (this.data.item?.id) {
      this.deleteEvent(this.data.item.id);
    }
  }

  modificarEvento(): void {
    this.data.esEditar = true;
    this.data.esVista = false;
    this.enableFormForEditing();
  }

  modificar(): void {
    this.update();
  }

  private enableFormForEditing(): void {
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      if (control?.disabled) {
        control.enable();
      }
    });
    this.updateTimeFieldsVisibility();
  }

  private getById(): void {
    if (!this.data.item?.id) return;

    this.spinner.show(SPINNER_NAMES.EVENTO_DIALOG);

    this.eventoAgendaService.getById(this.data.item.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.spinner.hide(SPINNER_NAMES.EVENTO_DIALOG))
      )
      .subscribe({
        next: (data) => {
          this.data.item = data;
          this.eventoAgendaDto = data;
          this.initializeForm(); // Reinicializar el formulario con los datos obtenidos
        },
        error: (error) => {
          this.snackBar.showError(error, 'Error al obtener el evento.');
        }
      });
  }

  openRepeatDialog(): void {
    const dialogRef = this.dialog.open(DialogRepeatEventComponent, {
      width: '400px',
      data: {
        diasSemana: this.form.get('DiasSemana')?.value,
        unidadRepeticion: this.form.get('UnidadRepeticion')?.value
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          this.form.patchValue({
            DiasSemana: result.diasSemana,
            UnidadRepeticion: result.unidadRepeticion
          });
        }
      });
  }

  cancelar(): void {
    this.dialogRef.close({ event: AccionesDialog.Cancelar, data: null });
  }

  sendEmail(): void {
    // Implementar funcionalidad de envío de email
    this.snackBar.showInfo('Función de envío de email en desarrollo', 'Información');
  }

  // ============================================
  // MÉTODOS DE ACTUALIZACIÓN ESPECÍFICOS
  // ============================================

  updateTitle(): void {
    if (!this.data.item?.id || !this.form.get('Titulo')?.valid) return;

    const titulo = this.form.get('Titulo')?.value;
    this.spinner.show(SPINNER_NAMES.EVENTO_DIALOG);

    this.eventoAgendaService.updateEventTitleAsync(this.data.item.id, titulo)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.spinner.hide(SPINNER_NAMES.EVENTO_DIALOG))
      )
      .subscribe({
        next: (success) => {
          if (success) {
            this.snackBar.showSuccess('Título actualizado exitosamente.', 'Éxito');
            this.data.item!.titulo = titulo;
          }
        },
        error: (error) => this.snackBar.showError(error, 'Error al actualizar el título.')
      });
  }

  updateLocation(): void {
    if (!this.data.item?.id) return;

    const lugar = this.form.get('Lugar')?.value || '';
    this.spinner.show(SPINNER_NAMES.EVENTO_DIALOG);

    this.eventoAgendaService.updateEventLocationAsync(this.data.item.id, lugar)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.spinner.hide(SPINNER_NAMES.EVENTO_DIALOG))
      )
      .subscribe({
        next: (success) => {
          if (success) {
            this.snackBar.showSuccess('Ubicación actualizada exitosamente.', 'Éxito');
            this.data.item!.lugar = lugar;
          }
        },
        error: (error) => this.snackBar.showError(error, 'Error al actualizar la ubicación.')
      });
  }

  updateEventType(): void {
    if (!this.data.item?.id || !this.form.get('IdTipoEvento')?.valid) return;

    const idTipoEvento = this.form.get('IdTipoEvento')?.value;
    this.spinner.show(SPINNER_NAMES.EVENTO_DIALOG);

    this.eventoAgendaService.updateEventTypeAsync(this.data.item.id, idTipoEvento)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.spinner.hide(SPINNER_NAMES.EVENTO_DIALOG))
      )
      .subscribe({
        next: (success) => {
          if (success) {
            this.snackBar.showSuccess('Tipo de evento actualizado exitosamente.', 'Éxito');
            const tipoEvento = this.listaTiposEvento.find(t => t.id === idTipoEvento);
            if (tipoEvento) {
              this.data.item!.tipoEventoAgenda = tipoEvento;
            }
          }
        },
        error: (error) => this.snackBar.showError(error, 'Error al actualizar el tipo de evento.')
      });
  }

  updateEventStatus(): void {
    if (!this.data.item?.id || !this.form.get('IdEstadoEvento')?.valid) return;

    const idEstadoEvento = this.form.get('IdEstadoEvento')?.value;
    this.spinner.show(SPINNER_NAMES.EVENTO_DIALOG);

    this.eventoAgendaService.updateEventStatusAsync(this.data.item.id, idEstadoEvento)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.spinner.hide(SPINNER_NAMES.EVENTO_DIALOG))
      )
      .subscribe({
        next: (success) => {
          if (success) {
            this.snackBar.showSuccess('Estado actualizado exitosamente.', 'Éxito');
            const estadoEvento = this.estadosEventoAgenda.find(e => e.id === idEstadoEvento);
            if (estadoEvento) {
              this.data.item!.estadoEventoAgenda = estadoEvento;
            }
          }
        },
        error: (error) => this.snackBar.showError(error, 'Error al actualizar el estado.')
      });
  }

  updateDates(): void {
    if (!this.data.item?.id || !this.form.get('FechaInicio')?.valid) return;

    const fechaInicio = new Date(this.form.get('FechaInicio')?.value);
    const fechaFin = this.form.get('FechaFin')?.value ?
      new Date(this.form.get('FechaFin')?.value) : fechaInicio;

    this.spinner.show(SPINNER_NAMES.EVENTO_DIALOG);

    this.eventoAgendaService.updateEventDateAsync(this.data.item.id, fechaInicio, fechaFin)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.spinner.hide(SPINNER_NAMES.EVENTO_DIALOG))
      )
      .subscribe({
        next: (success) => {
          if (success) {
            this.snackBar.showSuccess('Fechas actualizadas exitosamente.', 'Éxito');
            this.data.item!.fechaInicio = fechaInicio.toISOString();
            this.data.item!.fechaFin = fechaFin.toISOString();
          }
        },
        error: (error) => this.snackBar.showError(error, 'Error al actualizar las fechas.')
      });
  }

  updateHours(): void {
    if (!this.data.item?.id || !this.form.get('HoraInicio')?.valid || !this.form.get('HoraFin')?.valid) return;

    const horaInicio = this.form.get('HoraInicio')?.value;
    const horaFin = this.form.get('HoraFin')?.value;

    this.spinner.show(SPINNER_NAMES.EVENTO_DIALOG);

    this.eventoAgendaService.updateEventHoursAsync(this.data.item.id, horaInicio, horaFin)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.spinner.hide(SPINNER_NAMES.EVENTO_DIALOG))
      )
      .subscribe({
        next: (success) => {
          if (success) {
            this.snackBar.showSuccess('Horarios actualizados exitosamente.', 'Éxito');
            this.data.item!.horaInicio = horaInicio;
            this.data.item!.horaFin = horaFin;
          }
        },
        error: (error) => this.snackBar.showError(error, 'Error al actualizar los horarios.')
      });
  }

  updateDateAndHours(): void {
    if (!this.data.item?.id) return;

    const fechaInicio = new Date(this.form.get('FechaInicio')?.value);
    const fechaFin = this.form.get('FechaFin')?.value ?
      new Date(this.form.get('FechaFin')?.value) : fechaInicio;
    const horaInicio = this.form.get('HoraInicio')?.value || '00:00:00';
    const horaFin = this.form.get('HoraFin')?.value || '23:59:59';

    this.spinner.show(SPINNER_NAMES.EVENTO_DIALOG);

    this.eventoAgendaService.updateEventDateAndHoursAsync(
      this.data.item.id, fechaInicio, fechaFin, horaInicio, horaFin
    ).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.spinner.hide(SPINNER_NAMES.EVENTO_DIALOG))
    ).subscribe({
      next: (success) => {
        if (success) {
          this.snackBar.showSuccess('Fechas y horarios actualizados exitosamente.', 'Éxito');
          this.data.item!.fechaInicio = fechaInicio.toISOString();;
          this.data.item!.fechaFin = fechaFin.toISOString();;
          this.data.item!.horaInicio = horaInicio;
          this.data.item!.horaFin = horaFin;
        }
      },
      error: (error) => this.snackBar.showError(error, 'Error al actualizar fechas y horarios.')
    });
  }

  assignReminder(): void {
    if (!this.data.item?.id) return;

    const reminderValue = this.form.get('Recordatorio')?.value || 0;
    const reminderTime = this.convertMinutesToTimeSpan(reminderValue);

    this.spinner.show(SPINNER_NAMES.EVENTO_DIALOG);

    this.eventoAgendaService.assignReminderAsync(this.data.item.id, reminderTime)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.spinner.hide(SPINNER_NAMES.EVENTO_DIALOG))
      )
      .subscribe({
        next: (success) => {
          if (success) {
            this.snackBar.showSuccess('Recordatorio asignado exitosamente.', 'Éxito');
            this.data.item!.avisoNMinutosAntes = reminderValue;
          }
        },
        error: (error) => this.snackBar.showError(error, 'Error al asignar recordatorio.')
      });
  }

  markAsCompleted(): void {
    if (!this.data.item?.id) return;

    this.spinner.show(SPINNER_NAMES.EVENTO_DIALOG);

    this.eventoAgendaService.markAsCompletedAsync(this.data.item.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.spinner.hide(SPINNER_NAMES.EVENTO_DIALOG))
      )
      .subscribe({
        next: (success) => {
          if (success) {
            this.snackBar.showSuccess('Evento marcado como completado.', 'Éxito');
            // Actualizar el estado en el objeto local
            const estadoCompletado = this.estadosEventoAgenda.find(e =>
              e.descripcion?.toLowerCase().includes('completado') ||
              e.descripcion?.toLowerCase().includes('finalizado')
            );
            if (estadoCompletado) {
              this.data.item!.estadoEventoAgenda = estadoCompletado;
              this.form.patchValue({ IdEstadoEvento: estadoCompletado.id });
            }
          }
        },
        error: (error) => this.snackBar.showError(error, 'Error al marcar como completado.')
      });
  }

  cancelEvent(): void {
    if (!this.data.item?.id) return;

    this.spinner.show(SPINNER_NAMES.EVENTO_DIALOG);

    this.eventoAgendaService.cancelEventAsync(this.data.item.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.spinner.hide(SPINNER_NAMES.EVENTO_DIALOG))
      )
      .subscribe({
        next: (success) => {
          if (success) {
            this.snackBar.showSuccess('Evento cancelado exitosamente.', 'Éxito');
            // Actualizar el estado en el objeto local
            const estadoCancelado = this.estadosEventoAgenda.find(e =>
              e.descripcion?.toLowerCase().includes('cancelado')
            );
            if (estadoCancelado) {
              this.data.item!.estadoEventoAgenda = estadoCancelado;
              this.form.patchValue({ IdEstadoEvento: estadoCancelado.id });
            }
          }
        },
        error: (error) => this.snackBar.showError(error, 'Error al cancelar el evento.')
      });
  }

  addComment(): void {
    if (!this.data.item?.id) return;

    const comment = this.form.get('Observaciones')?.value || '';
    this.spinner.show(SPINNER_NAMES.EVENTO_DIALOG);

    this.eventoAgendaService.addCommentToEventAsync(this.data.item.id, comment)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.spinner.hide(SPINNER_NAMES.EVENTO_DIALOG))
      )
      .subscribe({
        next: (success) => {
          if (success) {
            this.snackBar.showSuccess('Comentario agregado exitosamente.', 'Éxito');
            this.data.item!.comentarios = comment;
          }
        },
        error: (error) => this.snackBar.showError(error, 'Error al agregar comentario.')
      });
  }

  // ============================================
  // MÉTODOS DE ACTUALIZACIÓN RÁPIDA
  // ============================================

  quickUpdateField(fieldName: string): void {
    if (!this.data.item?.id) return;

    switch (fieldName) {
      case 'Titulo':
        this.updateTitle();
        break;
      case 'Lugar':
        this.updateLocation();
        break;
      case 'IdTipoEvento':
        this.updateEventType();
        break;
      case 'IdEstadoEvento':
        this.updateEventStatus();
        break;
      case 'FechaInicio':
      case 'FechaFin':
        if (this.form.get('EsTodoElDia')?.value) {
          this.updateDates();
        } else {
          this.updateDateAndHours();
        }
        break;
      case 'HoraInicio':
      case 'HoraFin':
        this.updateHours();
        break;
      case 'Recordatorio':
        this.assignReminder();
        break;
      case 'Observaciones':
        this.addComment();
        break;
    }
  }

  // ============================================
  // MÉTODOS DE UTILIDAD
  // ============================================

  private convertMinutesToTimeSpan(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
  }

  combinarFechaYHora(fecha: string, hora: string, esTodoElDia: boolean, esFechaFin: boolean = false): string {
    if (!fecha) {
      console.warn('Fecha inválida:', fecha);
      return '';
    }

    let fechaHora: Date;

    if (esTodoElDia) {
      const horaFinal = esFechaFin ? TIME_CONSTANTS.END_OF_DAY : TIME_CONSTANTS.START_OF_DAY;
      fechaHora = new Date(`${fecha}T${horaFinal}`);
    } else {
      if (!hora) {
        console.warn('Hora inválida:', hora);
        return '';
      }
      const horaCompleta = hora.length === 5 ? `${hora}${TIME_CONSTANTS.DEFAULT_SECONDS}` : hora;
      fechaHora = new Date(`${fecha}T${horaCompleta}`);
    }

    return fechaHora.toISOString();
  }

  // ============================================
  // MÉTODOS DE UTILIDAD PARA LA INTERFAZ
  // ============================================

  getStatusIcon(status?: string): string {
    if (!status) return 'help';

    const statusLower = status.toLowerCase();
    if (statusLower.includes('completado') || statusLower.includes('finalizado')) return 'check_circle';
    if (statusLower.includes('cancelado')) return 'cancel';
    if (statusLower.includes('activo') || statusLower.includes('en curso')) return 'play_circle';
    if (statusLower.includes('pendiente')) return 'schedule';
    return 'help';
  }

  getActionIcon(): string {
    if (this.data.esEliminar) return 'delete';
    if (this.data.esNuevo) return 'add';
    if (this.data.esEditar) return 'edit';
    if (this.data.esVista) return 'visibility';
    return 'event';
  }

  getPriorityClass(priority?: number): string {
    switch (priority) {
      case 1: return 'low';
      case 2: return 'medium';
      case 3: return 'high';
      case 4: return 'critical';
      default: return 'medium';
    }
  }

  getPriorityText(priority?: number): string {
    switch (priority) {
      case 1: return 'Baja';
      case 2: return 'Media';
      case 3: return 'Alta';
      case 4: return 'Crítica';
      default: return 'Media';
    }
  }

  calculateDuration(): string {
    const horaInicio = this.form.get('HoraInicio')?.value;
    const horaFin = this.form.get('HoraFin')?.value;

    if (!horaInicio || !horaFin) return 'No definida';

    const [hInicio, mInicio] = horaInicio.split(':').map(Number);
    const [hFin, mFin] = horaFin.split(':').map(Number);

    const minutosInicio = hInicio * 60 + mInicio;
    const minutosFin = hFin * 60 + mFin;

    if (minutosFin <= minutosInicio) return 'Horario inválido';

    const duracionMinutos = minutosFin - minutosInicio;
    const horas = Math.floor(duracionMinutos / 60);
    const minutos = duracionMinutos % 60;

    if (horas === 0) return `${minutos} min`;
    if (minutos === 0) return `${horas} h`;
    return `${horas} h ${minutos} min`;
  }

  hasRepetition(): boolean {
    return !!(this.form.get('DiasSemana')?.value || this.form.get('UnidadRepeticion')?.value);
  }

  // ============================================
  // MÉTODOS DE ACCIONES ADICIONALES
  // ============================================

  duplicateEvent(): void {
    if (!this.data.item) return;

    const duplicatedData = {
      ...this.data.item,
      id: null,
      titulo: `Copia de ${this.data.item.titulo}`,
      fechaInicio: new Date(),
      fechaFin: this.data.item.esTodoElDia ? new Date() : null
    };

    // Crear nuevo diálogo para el evento duplicado
    const dialogRef = this.dialog.open(EventDialogCalendarComponentComponent, {
      width: '90vw',
      maxWidth: '1200px',
      data: {
        start: new Date(),
        end: null,
        esNuevo: true,
        item: duplicatedData,
        idExpediente: this.data.idExpediente
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result?.event === AccionesDialog.Agregar) {
          this.snackBar.showSuccess('Evento duplicado exitosamente', 'Éxito');
        }
      });
  }

  exportEvent(): void {
    if (!this.data.item) return;

    // Crear archivo .ics para exportar el evento
    const event = this.data.item;
    const startDate = new Date(event.fechaInicio!);
    const endDate = event.fechaFin ? new Date(event.fechaFin) : startDate;

    const icsContent = this.generateICSContent(event, startDate, endDate);
    this.downloadICS(icsContent, `evento-${event.titulo?.replace(/[^a-zA-Z0-9]/g, '-')}.ics`);
  }

  private generateICSContent(event: any, startDate: Date, endDate: Date): string {
    const formatDate = (date: Date, isAllDay: boolean = false): string => {
      if (isAllDay) {
        return date.toISOString().split('T')[0].replace(/-/g, '');
      }
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const startDateTime = formatDate(startDate, event.esTodoElDia);
    const endDateTime = formatDate(endDate, event.esTodoElDia);
    const now = formatDate(new Date());

    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Sistema de Agenda//NONSGML v1.0//ES',
      'BEGIN:VEVENT',
      `UID:${event.id}@agenda.sistema.com`,
      `DTSTAMP:${now}`,
      `DTSTART${event.esTodoElDia ? ';VALUE=DATE' : ''}:${startDateTime}`,
      `DTEND${event.esTodoElDia ? ';VALUE=DATE' : ''}:${endDateTime}`,
      `SUMMARY:${event.titulo}`,
      event.descripcion ? `DESCRIPTION:${event.descripcion}` : '',
      event.lugar ? `LOCATION:${event.lugar}` : '',
      `STATUS:${event.estadoEventoAgenda?.descripcion?.toUpperCase() || 'CONFIRMED'}`,
      event.avisoNMinutosAntes > 0 ? `BEGIN:VALARM\nTRIGGER:-PT${event.avisoNMinutosAntes}M\nACTION:DISPLAY\nDESCRIPTION:Recordatorio\nEND:VALARM` : '',
      'END:VEVENT',
      'END:VCALENDAR'
    ].filter(line => line).join('\n');
  }

  private downloadICS(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }

  saveAsDraft(): void {
    if (!this.form.get('Titulo')?.value) {
      this.snackBar.showError('El título es requerido para guardar como borrador', 'Error');
      return;
    }

    // Implementar lógica para guardar como borrador
    // Por ahora, guardamos con estado "Borrador"
    const estadoBorrador = this.estadosEventoAgenda.find(e =>
      e.descripcion?.toLowerCase().includes('borrador') ||
      e.descripcion?.toLowerCase().includes('draft')
    );

    if (estadoBorrador) {
      this.form.patchValue({ IdEstadoEvento: estadoBorrador.id });
    }

    this.create();
  }

  resetForm(): void {
    if (this.data.item?.id) {
      this.getById();
    } else {
      this.initializeForm();
    }
    this.snackBar.showInfo('Formulario restablecido', 'Información');
  }

  // ============================================
  // MÉTODOS DE VALIDACIÓN PARA TABS
  // ============================================

  hasErrorsInBasicInfo(): boolean {
    const titleControl = this.form.get('Titulo');
    const typeControl = this.form.get('IdTipoEvento');
    const statusControl = this.form.get('IdEstadoEvento');

    return !!(titleControl?.invalid && titleControl?.touched) ||
           !!(typeControl?.invalid && typeControl?.touched) ||
           !!(statusControl?.invalid && statusControl?.touched);
  }

  hasErrorsInSchedule(): boolean {
    const startDateControl = this.form.get('FechaInicio');
    const endDateControl = this.form.get('FechaFin');
    const startTimeControl = this.form.get('HoraInicio');
    const endTimeControl = this.form.get('HoraFin');

    return !!(startDateControl?.invalid && startDateControl?.touched) ||
           !!(endDateControl?.invalid && endDateControl?.touched) ||
           !!(startTimeControl?.invalid && startTimeControl?.touched) ||
           !!(endTimeControl?.invalid && endTimeControl?.touched) ||
           !!this.form.errors;
  }

  // Getters for template
  get isFormValid(): boolean {
    return this.form.valid;
  }

  get canEdit(): boolean {
    return !this.data.esVista || !!this.data.esEditar;
  }

  get dialogTitle(): string {
    if (this.data.esEliminar) return 'Eliminar Evento';
    if (this.data.esVista) return 'Ver Evento';
    if (this.data.esNuevo) return 'Crear Evento';
    if (this.data.esEditar) return 'Modificar Evento';
    return 'Evento';
  }

  get actionText(): string {
    if (this.data.esEliminar) return 'Eliminar';
    if (this.data.esVista) return 'Ver';
    if (this.data.esNuevo) return 'Crear';
    if (this.data.esEditar) return 'Modificar';
    return '';
  }

  validarFechas(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const fechaInicio = control.get('FechaInicio')?.value;
      const fechaFin = control.get('FechaFin')?.value;

      if (!fechaInicio || !fechaFin) return null;

      const inicio = new Date(fechaInicio);
      const fin = new Date(fechaFin);

      if (fin < inicio) {
        return { fechaInvalida: 'La fecha de fin no puede ser anterior a la fecha de inicio.' };
      }

      return null;
    };
  }

  validarHoras(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const fechaInicio = control.get('FechaInicio')?.value;
      const fechaFin = control.get('FechaFin')?.value;
      const horaInicio = control.get('HoraInicio')?.value;
      const horaFin = control.get('HoraFin')?.value;
      const esTodoElDia = control.get('EsTodoElDia')?.value;

      if (esTodoElDia || !fechaInicio || !horaInicio || !horaFin) return null;

      // Si no hay fecha de fin o es la misma fecha, validar horas
      if (!fechaFin || new Date(fechaInicio).toDateString() === new Date(fechaFin).toDateString()) {
        const [hInicio, mInicio] = horaInicio.split(':').map((n: string) => parseInt(n, 10));
        const [hFin, mFin] = horaFin.split(':').map((n: string) => parseInt(n, 10));

        const totalMinutosInicio = hInicio * 60 + mInicio;
        const totalMinutosFin = hFin * 60 + mFin;

        if (totalMinutosFin <= totalMinutosInicio) {
          return { horaInvalida: 'La hora de fin debe ser posterior a la hora de inicio.' };
        }
      }

      return null;
    };
  }
}
